import React, { useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { ScrollArea } from "../components/ui/scroll-area";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Loader2 } from "lucide-react";
import { cn } from "../lib/utils";

const AnimatedChatUI = ({
  messages,
  loading,
  streaming,
  loadingMessage,
  userInput,
  setUserInput,
  handleSend,
  scrollRef,
}) => {
  const messagesEndRef = useRef(null);
  
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, loading, streaming]);
  
  return (
    <motion.div
      key="chatUI"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3 }}
      className="w-full max-w-[90vw] md:max-w-[1400px] h-[75vh] p-4 md:p-6 lg:p-8 flex flex-col shadow-lg rounded-lg bg-white/70 dark:bg-gray-900/70 backdrop-blur-sm border border-gray-100/30 dark:border-gray-700/50"
    >
      <div className="flex-1 overflow-y-auto p-2 rounded-lg bg-white/60 dark:bg-gray-900/60 backdrop-blur-sm">
        <div className="flex flex-col space-y-4 p-2 rounded-lg min-h-full">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex w-full my-4 ${
                msg.role === "user" ? "justify-end" : "justify-start"
              }`}
            >
              {/* Message bubble with role label inside */}
              <div
                className={`p-4 max-w-[75%] rounded-lg whitespace-pre-wrap shadow-md border ${
                  msg.role === "user"
                    ? "bg-blue-200/90 text-gray-800 dark:bg-blue-700/80 dark:text-white border-blue-300/50 dark:border-blue-600/30 backdrop-blur-sm"
                    : "bg-gray-200/80 text-gray-900 dark:bg-gray-700/80 dark:text-white border-gray-300/50 dark:border-gray-600/50 backdrop-blur-sm"
                }`}
              >
                <div className="font-medium mb-2">
                  {msg.role === "user" ? "You:" : "CoachAI:"}
                </div>
                {msg.content}
                {streaming && i === messages.length - 1 && msg.role === "assistant" && (
                  <span className="inline-block ml-1 animate-pulse">▌</span>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="p-4 bg-gray-200/80 text-gray-900 dark:bg-gray-700/80 dark:text-white rounded-lg animate-pulse flex items-center gap-2 shadow-md border border-gray-300/50 dark:border-gray-600/50 backdrop-blur-sm">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                {loadingMessage}
              </div>
            </div>
          )}

          {/* Keep the original scrollRef for compatibility */}
          <div ref={scrollRef} style={{ display: 'none' }} />
          
          {/* Add our new scroll anchor at the end of messages */}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="mt-4 flex w-full gap-3">
        <Input
          value={userInput}
          onChange={(e) => setUserInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Ask me a question..."
          className="flex-1 h-12 px-4 text-base border rounded-lg bg-gray-200/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 border-gray-300/50 dark:border-gray-600/50"
        />
        <Button
          onClick={() => handleSend()}
          className="bg-gray-200/80 hover:bg-gray-300/80 dark:bg-gray-700/80 dark:hover:bg-gray-600/80 h-12 w-12 rounded-lg flex items-center justify-center border border-gray-300/50 dark:border-gray-600/50 backdrop-blur-sm shadow-md"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-gray-700 dark:text-white">
            <path d="M5 12h14"></path>
            <path d="m12 5 7 7-7 7"></path>
          </svg>
        </Button>
      </div>
    </motion.div>
  );
};

export default AnimatedChatUI;
